import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { cn } from '@zealot/ui/lib/utils';
import type * as React from 'react';

const Popover = ({ ...props }: PopoverPrimitive.Root.Props) => {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
};

const PopoverTrigger = ({ ...props }: PopoverPrimitive.Trigger.Props) => {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
};

// gloss
const popoverTransformMotion =
  'data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:zoom-in-95 data-closed:zoom-out-95';

const PopoverContent = ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  motion = 'scale',
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> & {
    motion?: 'scale' | 'fade';
  }) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            'data-open:fade-in-0 data-closed:fade-out-0 z-50 flex w-72 origin-(--transform-origin) flex-col gap-4 rounded-lg bg-popover p-2.5 text-popover-foreground text-xs shadow-md outline-hidden ring-1 ring-foreground/10 duration-100 data-closed:animate-out data-open:animate-in [&>[data-slot=popover-arrow]]:fill-popover',
            motion === 'scale' && popoverTransformMotion,
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};

const PopoverHeader = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-xs', className)} {...props} />;
};

const PopoverTitle = ({ className, ...props }: PopoverPrimitive.Title.Props) => {
  return (
    <PopoverPrimitive.Title data-slot="popover-title" className={cn('font-medium text-sm', className)} {...props} />
  );
};

const PopoverDescription = ({ className, ...props }: PopoverPrimitive.Description.Props) => {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
};

const PopoverClose = ({ ...props }: PopoverPrimitive.Close.Props) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
};

const PopoverArrow = ({ className, ...props }: PopoverPrimitive.Arrow.Props) => {
  return (
    <PopoverPrimitive.Arrow
      data-slot="popover-arrow"
      className={cn(
        'data-[side=bottom]:top-[-8px] data-[side=left]:right-[-8px] data-[side=top]:bottom-[-8px] data-[side=right]:left-[-8px]',
        className,
      )}
      {...props}
    >
      <svg width="20" height="10" viewBox="0 0 20 10" className="fill-popover drop-shadow-[0_1px_0_rgba(0,0,0,0.1)]">
        <polygon points="0,10 10,0 20,10" />
      </svg>
    </PopoverPrimitive.Arrow>
  );
};

export {
  Popover,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
